import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  RefreshControl,
  ScrollView,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';
import Animated, {
  FadeInDown,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { RootState, AppDispatch } from '@/store';
import {
  getClockStatus,
  clockIn,
  clockOut,
  getPayPeriodSummary,
  getPunchHistory,
  requestLocationPermission,
  clearError,
} from '@/store/slices/timeClockSlice';
import { TimeEntryResponse } from '@/types/api';
import { getTodayShifts, getPendingExceptions } from '@/store/slices/scheduleSlice';
import { mapShifts, nextClockInShiftKey, ShiftState } from '@/utils/shifts';
import {
  coverageForPunch,
  exceptionPrefillForPunch,
  exceptionPrefillForRange,
  ExceptionPrefill,
  PunchCoverage,
  windowsFromShifts,
} from '@/utils/punchCoverage';
import TimesheetScreen from '@/screens/TimesheetScreen';
import RequestTimeModal from '@/screens/schedule/RequestTimeModal';
import AddMissingPunchModal, { MissingPunchTarget } from '@/components/AddMissingPunchModal';
import SwitchPunchModal, { SwitchTarget } from '@/components/SwitchPunchModal';
import SplitPunchModal, { SplitTarget } from '@/components/SplitPunchModal';
import DoublePunchNotice from '@/components/DoublePunchNotice';
import { findDoublePunches } from '@/utils/punchAnomalies';
import { buildTimeline, currentEvent, shiftAfterClockOut } from '@/utils/todayTimeline';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import LoadingScreen from '@/components/common/LoadingScreen';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const fmtTime = (d: Date): string => format(d, 'h:mm a');

/**
 * The NFC tap-to-clock entry point. Hidden until NFC tags are deployed at the
 * gyms — the screen, service and route stay intact; flip this to show the link.
 */
const SHOW_NFC_LINK = false;

const STATE_CHIP: Record<ShiftState, string> = {
  UPCOMING: 'Upcoming',
  LIVE: 'Now',
  DONE: 'Done',
  MISSED: 'Missed',
};

/** Pay-period range to request, endDate clamped to today (future 400s / is empty). */
const rangeFor = (
  summary?: { startDate?: string; endDate?: string } | null
): { startDate: string; endDate: string } | null => {
  if (!summary?.startDate || !summary?.endDate) return null;
  const today = format(new Date(), 'yyyy-MM-dd');
  return { startDate: summary.startDate, endDate: summary.endDate > today ? today : summary.endDate };
};

/** A paired punch for "today", from the granular `/timesheet/range` data. */
interface DayPunch {
  id: number;
  inDate: Date;
  outDate: Date | null;
  /** The clock-out punch's own id (null while on the clock). */
  outId: number | null;
  displayIn: string;
  displayOut: string | null;
  minutes: number;
}

/** Pair today's timesheet entries (all punches) into DayPunch rows, oldest first. */
const toDayPunches = (entries: TimeEntryResponse[]): DayPunch[] =>
  entries
    .filter((e) => e.clockIn)
    .map((e) => {
      const inDate = parseISO(e.adjustedTime || e.actualTime);
      const outDate = e.pairedTime ? parseISO(e.pairedTime) : null;
      return {
        id: e.id,
        inDate,
        outDate,
        outId: e.pairedPunchId ?? null,
        displayIn: e.displayTime || fmtTime(inDate),
        displayOut: outDate ? fmtTime(outDate) : null,
        minutes: e.durationMinutes ?? 0,
      };
    })
    .sort((a, b) => a.inDate.getTime() - b.inDate.getTime());

/** What the Add-to-schedule sheet is covering, captured when it opens. */
interface ExceptionTarget {
  prefill: ExceptionPrefill;
  subtitle: string;
  /** Success banner text once the request is sent. */
  sent: string;
}

/** Coverage kinds the staffer can fix themselves by requesting exception time. */
const isFixable = (c?: PunchCoverage): c is Extract<PunchCoverage, { kind: 'uncovered' | 'ranLong' }> =>
  c?.kind === 'uncovered' || c?.kind === 'ranLong';

/** Sheet contents for an uncovered punch, or for the unscheduled tail of one. */
const exceptionTargetFor = (
  p: DayPunch,
  c: Extract<PunchCoverage, { kind: 'uncovered' | 'ranLong' }>,
  now: Date
): ExceptionTarget => {
  if (c.kind === 'ranLong') {
    return {
      prefill: exceptionPrefillForRange(c.gap.start, c.gap.end, now),
      subtitle:
        `You clocked out at ${p.displayOut}, but your schedule ended at ` +
        `${fmtTime(c.scheduledEnd)}. Add the extra time so it's paid.`,
      sent: `Added — your time after ${fmtTime(c.scheduledEnd)} is on your schedule.`,
    };
  }
  return {
    prefill: exceptionPrefillForPunch(p, now),
    subtitle: `You clocked in at ${p.displayIn} with nothing on your schedule. Add the time so it's paid.`,
    sent: `Added — your ${p.displayIn} punch is on your schedule.`,
  };
};

/** "29 minutes" / "1 minute"; an hour or more falls back to "1h 05m". */
const spelledDuration = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  return m >= 60 ? fmtDuration(m) : `${m} minute${m === 1 ? '' : 's'}`;
};

const fmtDuration = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) {
    return `${rem}m`;
  }
  return `${h}h ${rem.toString().padStart(2, '0')}m`;
};

const fmtCountdown = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) {
    return `starts in ${m}m`;
  }
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `starts in ${h}h` : `starts in ${h}h ${rem}m`;
};

// ---------------------------------------------------------------------------
// Live pulse dot
// ---------------------------------------------------------------------------

const LiveDot: React.FC = () => {
  const pulse = useSharedValue(0);
  useEffect(() => {
    pulse.value = withRepeat(withTiming(1, { duration: 1100 }), -1, true);
  }, [pulse]);
  const halo = useAnimatedStyle(() => ({
    opacity: 0.55 - pulse.value * 0.5,
    transform: [{ scale: 1 + pulse.value * 1.6 }],
  }));
  return (
    <View style={styles.dotWrap}>
      <Animated.View style={[styles.dotHalo, halo]} />
      <View style={styles.dotCore} />
    </View>
  );
};

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

const TimeClockScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation<any>();
  const { clockStatus, isLoading, error, payPeriodSummary, punchHistory } = useSelector(
    (state: RootState) => state.timeClock
  );
  const { todayShifts, isLoading: scheduleLoading } = useSelector(
    (state: RootState) => state.schedule
  );

  const [now, setNow] = useState(() => new Date());
  // On-page punch feedback (replaces the confirm/alert popups).
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );
  // Dedicated pull-to-refresh flag — the slice `isLoading` only tracks one
  // timeClock request, so it would drop the spinner while the schedule fetch
  // (a different slice) is still in flight. This tracks all refresh dispatches.
  const [refreshing, setRefreshing] = useState(false);
  // The unscheduled time being covered by an exception request (sheet open when set).
  // The prefill is captured on open so the ticking clock doesn't re-seed the form.
  const [exceptionTarget, setExceptionTarget] = useState<ExceptionTarget | null>(null);
  // Fixes for two clock-ins / clock-outs in a row today.
  const [switchTarget, setSwitchTarget] = useState<SwitchTarget | null>(null);
  const [missingTarget, setMissingTarget] = useState<MissingPunchTarget | null>(null);
  const [splitTarget, setSplitTarget] = useState<SplitTarget | null>(null);

  const load = useCallback(() => {
    dispatch(getTodayShifts());
    dispatch(getClockStatus());
    dispatch(getPayPeriodSummary());
  }, [dispatch]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const range = rangeFor(payPeriodSummary);
      await Promise.all([
        dispatch(getTodayShifts()),
        dispatch(getClockStatus()),
        dispatch(getPayPeriodSummary()),
        ...(range ? [dispatch(getPunchHistory(range))] : []),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate]);

  useEffect(() => {
    dispatch(requestLocationPermission());
  }, [dispatch]);

  // Refetch on focus (tab screens stay mounted). Also re-pull punches for the
  // pay-period range (same source the Pay Period timesheet uses, so Today's
  // list and Pay Period agree); filtered to today for the Today list below.
  useFocusEffect(
    useCallback(() => {
      load();
      const range = rangeFor(payPeriodSummary);
      if (range) dispatch(getPunchHistory(range));
    }, [load, dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate])
  );

  // First load before the pay-period range is known: fetch punches as soon as
  // the summary arrives.
  useEffect(() => {
    const range = rangeFor(payPeriodSummary);
    if (range) dispatch(getPunchHistory(range));
  }, [dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate]);

  // Keep the live timer current.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Surface slice errors (e.g. a failed punch) as an on-page banner, not a popup.
  useEffect(() => {
    if (error) {
      setFeedback({ type: 'error', text: error });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Auto-dismiss the feedback banner a few seconds after it appears.
  useEffect(() => {
    if (!feedback) return undefined;
    const id = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(id);
  }, [feedback]);

  const shifts = useMemo(() => mapShifts(todayShifts), [todayShifts]);

  // Today | Pay Period sub-view. Opens to the mode a caller requested (e.g. the
  // Dashboard "Timesheet" shortcut → Pay Period) and syncs if it changes.
  const route = useRoute<any>();
  const requestedMode = route.params?.mode as 'today' | 'pay' | undefined;
  const [mode, setMode] = useState<'today' | 'pay'>(requestedMode === 'pay' ? 'pay' : 'today');
  // Apply a requested sub-view on every focus (the tab stays mounted and RN
  // merges params, so a value-change effect misses repeat taps of the same
  // Dashboard shortcut). Consume-and-clear so manual toggling isn't overridden.
  useFocusEffect(
    useCallback(() => {
      const m = route.params?.mode as 'today' | 'pay' | undefined;
      if (m) {
        setMode(m);
        navigation.setParams({ mode: undefined });
      }
    }, [route.params?.mode, navigation])
  );

  // All of today's punches (granular, from the pay-period range), paired + sorted.
  const dayPunches = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return toDayPunches(punchHistory.filter((e) => e.punchDate === today));
  }, [punchHistory]);

  // Scheduled event windows for the day, from the paired shift data.
  const scheduledWindows = useMemo(() => windowsFromShifts(shifts), [shifts]);

  // Per-punch coverage (id → coverage), for badges on the punch list.
  const punchCoverageById = useMemo(() => {
    const map = new Map<number, PunchCoverage>();
    for (const p of dayPunches) {
      const c = coverageForPunch(p, scheduledWindows);
      if (c) map.set(p.id, c);
    }
    return map;
  }, [dayPunches, scheduledWindows]);

  // Orphan clock-outs today — a clock-out with no matching clock-in (admin-added,
  // or the clock-in is on a prior day). toDayPunches is clock-in-anchored, so these
  // would otherwise be silently hidden (#31); surface them as notices below.
  const unpairedOutsToday = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return punchHistory.filter((e) => e.punchDate === today && !e.clockIn && !e.pairedPunchId);
  }, [punchHistory]);

  const openException = (punch: DayPunch) => {
    const c = punchCoverageById.get(punch.id);
    if (isFixable(c)) setExceptionTarget(exceptionTargetFor(punch, c, new Date()));
  };

  // The requested time joins today's shifts on refresh; also refresh the Schedule
  // tab's requested-time list.
  const onExceptionSubmitted = () => {
    setFeedback({ type: 'success', text: exceptionTarget?.sent ?? 'Added to your schedule.' });
    afterAction();
    dispatch(getPendingExceptions());
  };

  const doublesToday = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return findDoublePunches(punchHistory).filter((d) => d.date === today);
  }, [punchHistory]);

  // Clock state: the open punch (today's timesheet) is the source of truth; the
  // shift data's LIVE state is a fallback while the timesheet loads.
  const openPunch = useMemo(() => dayPunches.find((p) => !p.outDate) ?? null, [dayPunches]);
  const liveShift = useMemo(() => shifts.find((s) => s.state === 'LIVE'), [shifts]);
  const isClockedIn = !!openPunch || !!liveShift;
  const liveSince = openPunch?.inDate ?? liveShift?.liveSince ?? null;

  // Recomputed each minute (via `now`) so the timeline and next-shift hint stay current.
  const nowMinute = Math.floor(now.getTime() / 60000);
  const timeline = useMemo(
    () => buildTimeline(shifts, dayPunches, new Date(nowMinute * 60000)),
    [shifts, dayPunches, nowMinute]
  );
  // A clock-out right before a shift with no clock-in may have been meant as that
  // clock-in (clocked out 12:29, class at 12:30): session id → that shift, and the
  // reverse so the missed shift can point at the fix.
  const { clockInFor, clockOutBefore } = useMemo(() => {
    const byPunch = new Map<number, Extract<(typeof timeline)[number], { kind: 'shift' }>>();
    const byShift = new Map<string, DayPunch>();
    timeline.forEach((g, gi) =>
      g.sessions.forEach((p) => {
        const next = shiftAfterClockOut(timeline, gi, p);
        if (next) {
          byPunch.set(p.id, next);
          byShift.set(next.key, p);
        }
      })
    );
    return { clockInFor: byPunch, clockOutBefore: byShift };
  }, [timeline]);

  const openSplit = (p: DayPunch, c: Extract<PunchCoverage, { kind: 'ranLong' }>) => {
    const next = clockInFor.get(p.id);
    if (!next || !p.outDate || p.outId === null) return;
    setSplitTarget({
      punchId: p.outId,
      punchTime: p.outDate,
      inTime: p.inDate,
      scheduledEnd: c.scheduledEnd,
      nextLabel: next.events.length === 1 ? next.events[0].label : next.label,
      nextEnd: next.end <= new Date() ? next.end : null,
    });
  };

  const liveGroup = openPunch
    ? timeline.find((g) => g.sessions.some((s) => s.id === openPunch.id))
    : undefined;
  const clockInTargetKey = useMemo(
    () => (isClockedIn ? null : nextClockInShiftKey(shifts, new Date(nowMinute * 60000))),
    [isClockedIn, shifts, nowMinute]
  );
  const nextShift = shifts.find((s) => s.key === clockInTargetKey);
  // Name the event (not just the shift's first one) for the status card.
  const nowDate = new Date(nowMinute * 60000);
  const liveEvent = liveGroup?.kind === 'shift' ? currentEvent(liveGroup.events, nowDate) : undefined;
  const nextGroup = timeline.find((g) => g.kind === 'shift' && g.key === nextShift?.key);
  const nextEvent = nextGroup?.kind === 'shift' ? currentEvent(nextGroup.events, nowDate) : undefined;

  const workedToday = dayPunches.reduce(
    (m, p) => m + (p.outDate ? p.minutes : (now.getTime() - p.inDate.getTime()) / 60000),
    0
  );
  const lastOut = dayPunches.reduce<Date | null>(
    (latest, p) => (p.outDate && (!latest || p.outDate > latest) ? p.outDate : latest),
    null
  );

  const afterAction = () => {
    dispatch(getTodayShifts());
    dispatch(getClockStatus());
    dispatch(getPayPeriodSummary());
    const range = rangeFor(payPeriodSummary);
    if (range) dispatch(getPunchHistory(range));
  };

  // No confirm popup — a tap punches immediately and the banner confirms it.
  // A failed punch sets slice `error`, which the effect above turns into an
  // error banner, so we only handle the success path here.
  const handleClockIn = async () => {
    try {
      await dispatch(clockIn({})).unwrap();
      setFeedback({ type: 'success', text: `Clocked in at ${format(new Date(), 'h:mm a')}` });
    } catch {
      // error surfaced via the slice-error banner
    } finally {
      afterAction();
    }
  };

  const handleClockOut = async () => {
    try {
      await dispatch(clockOut({})).unwrap();
      setFeedback({ type: 'success', text: `Clocked out at ${format(new Date(), 'h:mm a')}` });
    } catch {
      // error surfaced via the slice-error banner
    } finally {
      afterAction();
    }
  };

  if ((isLoading || scheduleLoading) && !clockStatus && !todayShifts) {
    return <LoadingScreen message="Loading your day…" />;
  }

  const payToDate = payPeriodSummary ? fmtDuration(payPeriodSummary.totalMinutesWorked) : null;

  return (
    <View style={styles.root}>
      {/* Today | Pay Period toggle */}
      <View style={styles.toggle}>
        <TouchableOpacity
          style={[styles.toggleBtn, mode === 'today' && styles.toggleBtnActive]}
          activeOpacity={0.8}
          onPress={() => setMode('today')}>
          <Text style={[styles.toggleText, mode === 'today' && styles.toggleTextActive]}>
            Today
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.toggleBtn, mode === 'pay' && styles.toggleBtnActive]}
          activeOpacity={0.8}
          onPress={() => setMode('pay')}>
          <Text style={[styles.toggleText, mode === 'pay' && styles.toggleTextActive]}>
            Pay Period
          </Text>
        </TouchableOpacity>
      </View>

      {mode === 'pay' ? (
        <TimesheetScreen />
      ) : (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      {/* Punch feedback banner (replaces the old confirm/alert popups) */}
      {feedback && (
        <View
          style={[
            styles.banner,
            feedback.type === 'success' ? styles.bannerSuccess : styles.bannerError,
          ]}>
          <Icon
            name={feedback.type === 'success' ? 'checkmark-circle' : 'alert-circle'}
            size={18}
            color={feedback.type === 'success' ? '#137333' : '#c5221f'}
          />
          <Text
            style={[
              styles.bannerText,
              { color: feedback.type === 'success' ? '#137333' : '#c5221f' },
            ]}>
            {feedback.text}
          </Text>
        </View>
      )}

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <View>
            <Text style={styles.kicker}>{format(now, 'EEEE').toUpperCase()}</Text>
            <Text style={styles.headline}>TODAY</Text>
          </View>
          <View style={styles.dateBadge}>
            <Text style={styles.dateMonth}>{format(now, 'MMM').toUpperCase()}</Text>
            <Text style={styles.dateDay}>{format(now, 'd')}</Text>
          </View>
        </View>
        <View style={styles.rule} />
        <View style={styles.payRow}>
          <Text style={styles.payValue}>{payToDate ?? '—'}</Text>
          <Text style={styles.payLabel}>PAY PERIOD TO DATE</Text>
        </View>
      </View>

      {/* NFC tap — entry point to the dedicated NFC screen (hidden; see SHOW_NFC_LINK) */}
      {SHOW_NFC_LINK && (
        <TouchableOpacity
          style={styles.nfcLink}
          activeOpacity={0.7}
          onPress={() => navigation.navigate('NfcClock')}>
          <Icon name="scan-outline" size={18} color={colors.inkSoft} />
          <Text style={styles.nfcLinkText}>Tap NFC to clock in / out</Text>
          <Icon name="chevron-forward" size={16} color={colors.inkFaint} />
        </TouchableOpacity>
      )}

      {/* Status — clock state and the one clock in / out button, independent of shifts */}
      <Animated.View
        entering={FadeInDown.duration(380)}
        style={[styles.statusCard, isClockedIn && styles.statusCardLive]}>
        {isClockedIn && <View style={styles.tape} />}
        <View style={styles.statusHead}>
          {isClockedIn ? (
            <View style={styles.livePill}>
              <LiveDot />
              <Text style={styles.livePillText}>ON THE CLOCK</Text>
            </View>
          ) : (
            <View style={styles.offPill}>
              <Text style={styles.offPillText}>OFF THE CLOCK</Text>
            </View>
          )}
          <Text style={styles.statusWorked}>{fmtDuration(workedToday)} today</Text>
        </View>

        {isClockedIn && liveSince ? (
          <>
            <View style={styles.timerRow}>
              <Text style={styles.timer}>
                {fmtDuration((now.getTime() - liveSince.getTime()) / 60000)}
              </Text>
              <Text style={styles.timerSub}>since {fmtTime(liveSince)}</Text>
            </View>
            {liveGroup?.kind === 'shift' ? (
              <Text style={styles.statusContext}>
                {liveEvent?.label ?? liveGroup.label} · until {fmtTime(liveEvent?.end ?? liveGroup.end)}
              </Text>
            ) : (
              <View style={styles.statusContextRow}>
                <Text style={[styles.statusContext, styles.statusContextWarn]}>Not on your schedule</Text>
                {openPunch && isFixable(punchCoverageById.get(openPunch.id)) && (
                  <TouchableOpacity
                    style={styles.inlineFix}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    onPress={() => openException(openPunch)}>
                    <Text style={styles.inlineFixText}>Add to schedule</Text>
                    <Icon name="chevron-forward" size={13} color={colors.accent} />
                  </TouchableOpacity>
                )}
              </View>
            )}
          </>
        ) : (
          <>
            <Text style={styles.statusLine}>
              {lastOut ? `Clocked out at ${fmtTime(lastOut)}` : 'Not clocked in yet today'}
            </Text>
            <Text style={styles.statusContext}>
              {nextShift?.state === 'DONE'
                ? `${nextEvent?.label ?? nextShift.label} runs until ${fmtTime(nextEvent?.end ?? nextShift.scheduledEnd)}`
                : nextShift
                  ? `Next: ${nextShift.label} at ${fmtTime(nextShift.scheduledStart)} · ${fmtCountdown(
                      (nextShift.scheduledStart.getTime() - now.getTime()) / 60000
                    )}`
                  : 'Nothing else on your schedule today'}
            </Text>
          </>
        )}

        <TouchableOpacity
          style={[styles.actionBtn, styles.actionBtnSolid, styles.statusBtn, isLoading && styles.btnBusy]}
          activeOpacity={0.85}
          disabled={isLoading}
          onPress={isClockedIn ? handleClockOut : handleClockIn}>
          <Icon name={isClockedIn ? 'stop-circle' : 'play-circle'} size={22} color={colors.onAccent} />
          <Text style={styles.actionBtnSolidText}>{isClockedIn ? 'CLOCK OUT' : 'CLOCK IN'}</Text>
        </TouchableOpacity>
      </Animated.View>

      {/* Notices — only things that need fixing and aren't shown on the timeline */}
      {clockStatus?.hasUnclosedPreviousPunch && clockStatus.unclosedPunchDate && (
        <View style={styles.alert}>
          <Icon name="warning" size={20} color={colors.warn} />
          <Text style={styles.alertText}>
            You have an unclosed punch from{' '}
            {format(new Date(clockStatus.unclosedPunchDate), 'EEE, MMM d')}. Add the missing
            clock-out on Pay Period.
          </Text>
        </View>
      )}
      {doublesToday.map((d) => (
        <DoublePunchNotice
          key={`double-${d.second.id}`}
          double={d}
          onSwitch={setSwitchTarget}
          onAddMissing={setMissingTarget}
        />
      ))}
      {unpairedOutsToday.map((e) => {
        const raw = e.adjustedTime || e.actualTime;
        const t = e.displayTime || (raw ? fmtTime(parseISO(raw)) : '');
        return (
          <View key={`orphan-${e.id}`} style={styles.alert}>
            <Icon name="warning" size={20} color={colors.warn} />
            <Text style={styles.alertText}>
              Your {t} clock-out has no clock-in. Add the missing clock-in on Pay Period.
            </Text>
          </View>
        );
      })}

      {/* Today — scheduled shifts with the work under them, and unscheduled work */}
      <Text style={styles.stackTitle}>Today</Text>
      {timeline.length === 0 ? (
        <Text style={styles.emptyLine}>Nothing on your schedule today.</Text>
      ) : (
        <View style={styles.timelineCard}>
          {timeline.map((g, gi) => (
            <View key={g.key} style={[styles.group, gi > 0 && styles.groupDivider]}>
              <View style={styles.groupHead}>
                <View style={styles.groupTitleWrap}>
                  {g.kind === 'shift' && g.events.length > 1 ? (
                    // Several events back to back: the shift is the time span, events listed below.
                    <Text style={styles.groupTitle}>
                      {fmtTime(g.start)} – {fmtTime(g.end)}
                    </Text>
                  ) : (
                    <>
                      <Text style={[styles.groupTitle, g.kind === 'unscheduled' && styles.groupTitleWarn]}>
                        {g.kind === 'shift' ? g.label : 'Not on your schedule'}
                      </Text>
                      {g.kind === 'shift' && (
                        <Text style={styles.groupRange}>
                          {fmtTime(g.start)} – {fmtTime(g.end)}
                        </Text>
                      )}
                    </>
                  )}
                </View>
                {g.kind === 'shift' && (
                  <View style={styles.chips}>
                    <View
                      style={[
                        styles.chip,
                        g.state === 'LIVE' && styles.chipLive,
                        g.state === 'MISSED' && styles.chipMissed,
                      ]}>
                      <Text
                        style={[
                          styles.chipText,
                          g.state === 'LIVE' && styles.chipTextLive,
                          g.state === 'MISSED' && styles.chipTextMissed,
                        ]}>
                        {STATE_CHIP[g.state]}
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              {g.kind === 'shift' && g.events.length > 1 && (
                <View style={styles.eventList}>
                  {g.events.map((ev) => (
                    <View key={ev.key} style={styles.eventRow}>
                      <Text style={styles.eventLabel} numberOfLines={1}>
                        {ev.label}
                      </Text>
                      <Text style={styles.eventTime}>
                        {fmtTime(ev.start)} – {fmtTime(ev.end)}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {g.kind === 'shift' && g.sessions.length === 0 && (
                <Text style={[styles.groupNote, g.state === 'MISSED' && styles.groupNoteWarn]}>
                  {g.state === 'MISSED'
                    ? clockOutBefore.has(g.key)
                      ? `No clock-in recorded. If your ${clockOutBefore.get(g.key)!.displayOut} punch was meant as this clock-in, fix it above.`
                      : 'No clock-in recorded. Add it on Pay Period.'
                    : g.state === 'UPCOMING'
                      ? g.start > now
                        ? fmtCountdown((g.start.getTime() - now.getTime()) / 60000)
                        : 'starting now'
                      : ''}
                </Text>
              )}

              {g.sessions.map((p) => {
                const cov = punchCoverageById.get(p.id);
                const open = !p.outDate;
                // "Not on your schedule" is the group heading for unscheduled work, and
                // running long gets its own panel below.
                const badge = cov?.kind === 'mismatch' ? cov : null;
                const next = clockInFor.get(p.id);
                return (
                  <View key={p.id} style={styles.session}>
                    <View style={styles.punchRow}>
                      <View style={styles.punchTimes}>
                        <Text style={styles.punchTime}>{p.displayIn}</Text>
                        <Icon name="arrow-forward" size={13} color={colors.inkFaint} />
                        <Text style={[styles.punchTime, open && styles.punchOpen]}>
                          {p.displayOut ?? 'on the clock'}
                        </Text>
                        {badge && (
                          <View style={styles.covBadge}>
                            <Text style={styles.covBadgeText}>{badge.label}</Text>
                          </View>
                        )}
                      </View>
                      <Text style={styles.punchDur}>{open ? '—' : fmtDuration(p.minutes)}</Text>
                    </View>
                    {cov?.kind === 'ranLong' && (
                      <View style={styles.unpaid}>
                        <View style={styles.unpaidHead}>
                          <Icon name="warning" size={16} color={colors.warn} />
                          <Text style={styles.unpaidText}>
                            {fmtTime(cov.gap.start)} – {fmtTime(cov.gap.end)} isn't on your schedule, so
                            those {spelledDuration((cov.gap.end.getTime() - cov.gap.start.getTime()) / 60000)}{' '}
                            won't be paid.
                          </Text>
                        </View>
                        <TouchableOpacity
                          style={styles.punchFix}
                          activeOpacity={0.85}
                          onPress={() => openException(p)}>
                          <Icon name="add-circle-outline" size={16} color={colors.accent} />
                          <Text style={styles.punchFixText}>
                            I was working — add {fmtTime(cov.gap.start)} – {fmtTime(cov.gap.end)} to my schedule
                          </Text>
                        </TouchableOpacity>
                        {next && (
                          <TouchableOpacity
                            style={styles.punchFix}
                            activeOpacity={0.85}
                            onPress={() => openSplit(p, cov)}>
                            <Icon name="swap-horizontal" size={16} color={colors.accent} />
                            <Text style={styles.punchFixText}>
                              I left at {fmtTime(cov.scheduledEnd)} — {p.displayOut} was my clock-in for{' '}
                              {next.events.length === 1 ? next.events[0].label : next.label}
                            </Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                    {cov?.kind === 'uncovered' && (
                      <>
                        <Text style={styles.unpaidNote}>Time that isn't on your schedule isn't paid.</Text>
                        <TouchableOpacity
                          style={styles.punchFix}
                          activeOpacity={0.85}
                          onPress={() => openException(p)}>
                          <Icon name="add-circle-outline" size={16} color={colors.accent} />
                          <Text style={styles.punchFixText}>Add this time to my schedule</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      )}

      <Text style={styles.footnote}>
        Paid hours are your clock in / out time that falls within the events on your schedule.
      </Text>
    </ScrollView>
      )}

      <RequestTimeModal
        visible={!!exceptionTarget}
        prefill={exceptionTarget?.prefill}
        subtitle={exceptionTarget?.subtitle}
        onClose={() => setExceptionTarget(null)}
        onSubmitted={onExceptionSubmitted}
      />
      <SwitchPunchModal
        target={switchTarget}
        onClose={() => setSwitchTarget(null)}
        onSwitched={(message) => {
          setFeedback({ type: 'success', text: message });
          afterAction();
        }}
      />
      <SplitPunchModal
        target={splitTarget}
        onClose={() => setSplitTarget(null)}
        onFixed={(message) => {
          setFeedback({ type: 'success', text: message });
          afterAction();
        }}
      />
      <AddMissingPunchModal
        visible={!!missingTarget}
        target={missingTarget}
        days={[format(new Date(), 'yyyy-MM-dd')]}
        onClose={() => setMissingTarget(null)}
        onSubmitted={(message) => {
          setFeedback({ type: 'success', text: message });
          afterAction();
        }}
      />
    </View>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  toggle: {
    flexDirection: 'row',
    gap: 6,
    padding: 4,
    margin: space.lg,
    marginBottom: 0,
    backgroundColor: colors.surfaceSunk,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.line,
  },
  toggleBtn: {
    flex: 1,
    paddingVertical: 9,
    borderRadius: radius.pill,
    alignItems: 'center',
  },
  toggleBtnActive: {
    backgroundColor: colors.surface,
    ...shadow.card,
  },
  toggleText: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.inkSoft,
  },
  toggleTextActive: {
    color: colors.ink,
  },
  punchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  punchFix: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: space.sm,
    paddingVertical: 8,
    paddingHorizontal: space.sm,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  punchFixText: {
    flexShrink: 1,
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
    textAlign: 'center',
  },
  unpaid: {
    marginTop: space.sm,
    padding: space.sm,
    borderRadius: radius.sm,
    backgroundColor: colors.warnSoft,
  },
  unpaidHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  unpaidText: {
    flex: 1,
    fontFamily: font.bodySemi,
    fontSize: 13.5,
    lineHeight: 19,
    color: colors.warn,
  },
  unpaidNote: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.warn,
    marginTop: 4,
  },
  punchTimes: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    flexWrap: 'wrap',
  },
  punchTime: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.ink,
  },
  punchOpen: {
    color: colors.accent,
    fontFamily: font.bodySemi,
  },
  covBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: colors.warnSoft,
    borderRadius: radius.sm,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  covBadgeText: {
    fontFamily: font.bodySemi,
    fontSize: 11,
    color: colors.warn,
  },
  punchDur: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.inkSoft,
    minWidth: 52,
    textAlign: 'right',
  },
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  content: {
    padding: space.lg,
    paddingBottom: space.xxl,
  },

  // Punch feedback banner
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginBottom: space.md,
  },
  bannerSuccess: {
    backgroundColor: '#e6f4ea',
    borderWidth: 1,
    borderColor: '#b7dfc2',
  },
  bannerError: {
    backgroundColor: '#fce8e6',
    borderWidth: 1,
    borderColor: '#f3c1bd',
  },
  bannerText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },

  // Header
  header: {
    marginBottom: space.lg,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  kicker: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2.5,
    color: colors.accent,
    marginBottom: -2,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 64,
    lineHeight: 64,
    color: colors.ink,
    letterSpacing: -1,
  },
  dateBadge: {
    backgroundColor: colors.ink,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    alignItems: 'center',
    minWidth: 62,
    marginTop: space.sm,
  },
  dateMonth: {
    fontFamily: font.bodyBold,
    fontSize: 12,
    letterSpacing: 2,
    color: colors.accent,
  },
  dateDay: {
    fontFamily: font.display,
    fontSize: 30,
    lineHeight: 32,
    color: colors.surface,
  },
  rule: {
    height: 3,
    backgroundColor: colors.ink,
    marginTop: space.md,
    marginBottom: space.md,
    width: 48,
    borderRadius: 2,
  },
  payRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  payValue: {
    fontFamily: font.display,
    fontSize: 30,
    lineHeight: 32,
    color: colors.ink,
  },
  payLabel: {
    fontFamily: font.bodySemi,
    fontSize: 11,
    letterSpacing: 1.8,
    color: colors.inkFaint,
    marginLeft: space.sm,
  },

  // NFC link — entry to the NFC tap screen (no longer its own bottom tab)
  nfcLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    backgroundColor: colors.surface,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.line,
    paddingVertical: 6,
    paddingHorizontal: 12,
    marginBottom: space.lg,
  },
  nfcLinkText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
  },

  // Alert
  alert: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.warnSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
    borderWidth: 1,
    borderColor: '#E8D49B',
  },
  alertText: {
    flex: 1,
    marginLeft: space.sm,
    fontFamily: font.bodyMed,
    fontSize: 13.5,
    lineHeight: 19,
    color: '#6E4E08',
  },

  // Stack
  stackTitle: {
    fontFamily: font.bodyBold,
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: colors.inkSoft,
    marginBottom: space.md,
  },


  // Live indicator + timer (status card)
  tape: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    backgroundColor: colors.accent,
  },
  livePill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: radius.pill,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  livePillText: {
    fontFamily: font.bodyBold,
    fontSize: 10.5,
    letterSpacing: 1.4,
    color: colors.accentDark,
    marginLeft: 7,
  },
  dotWrap: {
    width: 10,
    height: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotHalo: {
    position: 'absolute',
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.accent,
  },
  dotCore: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.accent,
  },
  timerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: space.md,
    marginBottom: space.lg,
  },
  timer: {
    fontFamily: font.display,
    fontSize: 52,
    lineHeight: 52,
    color: colors.accent,
    letterSpacing: -0.5,
  },
  timerSub: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.inkFaint,
    marginLeft: space.md,
  },




  // Action buttons
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
    borderRadius: radius.md,
  },
  actionBtnSolid: {
    backgroundColor: colors.accent,
  },
  actionBtnSolidText: {
    fontFamily: font.display,
    fontSize: 22,
    letterSpacing: 1,
    color: colors.onAccent,
    marginLeft: space.sm,
  },
  btnBusy: {
    opacity: 0.5,
  },



  // Status card — clock state + the single clock in / out button
  statusCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    marginBottom: space.md,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.line,
    ...shadow.card,
  },
  statusCardLive: {
    paddingLeft: space.lg + 6,
    borderColor: 'transparent',
    ...shadow.live,
  },
  statusHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.sm,
  },
  offPill: {
    backgroundColor: colors.surfaceSunk,
    borderRadius: radius.pill,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  offPillText: {
    fontFamily: font.bodyBold,
    fontSize: 10.5,
    letterSpacing: 1.4,
    color: colors.inkSoft,
  },
  statusWorked: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkSoft,
  },
  statusLine: {
    fontFamily: font.displayBold,
    fontSize: 22,
    color: colors.ink,
    marginTop: space.xs,
  },
  statusContextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  statusContext: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.inkSoft,
    marginTop: 2,
  },
  statusContextWarn: {
    color: colors.warn,
    fontFamily: font.bodySemi,
  },
  statusBtn: {
    marginTop: space.lg,
  },
  inlineFix: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  inlineFixText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accent,
  },

  // Today timeline
  timelineCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    marginBottom: space.md,
    ...shadow.card,
  },
  group: {
    paddingVertical: space.md,
  },
  groupDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  groupHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  groupTitleWrap: {
    flex: 1,
  },
  groupTitle: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
  },
  groupTitleWarn: {
    color: colors.warn,
  },
  groupRange: {
    fontFamily: font.bodyMed,
    fontSize: 13,
    color: colors.inkSoft,
    marginTop: 1,
  },
  groupNote: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.inkFaint,
    marginTop: space.xs,
  },
  groupNoteWarn: {
    color: colors.warn,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 4,
    maxWidth: '55%',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    borderRadius: radius.pill,
    paddingVertical: 3,
    paddingHorizontal: 8,
    backgroundColor: colors.surfaceSunk,
  },
  chipLive: {
    backgroundColor: colors.accentSoft,
  },
  chipMissed: {
    backgroundColor: colors.warnSoft,
  },
  chipText: {
    fontFamily: font.bodySemi,
    fontSize: 11,
    color: colors.inkSoft,
  },
  chipTextLive: {
    color: colors.accentDark,
  },
  chipTextMissed: {
    color: colors.warn,
  },
  session: {
    marginTop: space.sm,
  },
  eventList: {
    marginTop: space.xs,
    paddingLeft: space.sm,
    borderLeftWidth: 2,
    borderLeftColor: colors.line,
    gap: 2,
  },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  eventLabel: {
    flexShrink: 1,
    fontFamily: font.bodySemi,
    fontSize: 13.5,
    color: colors.ink,
  },
  eventTime: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.inkSoft,
  },
  emptyLine: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkFaint,
    marginBottom: space.md,
  },

  // Footnote
  footnote: {
    fontFamily: font.body,
    fontSize: 12,
    lineHeight: 17,
    color: colors.inkFaint,
    textAlign: 'center',
    marginTop: space.md,
    paddingHorizontal: space.lg,
  },
});

export default TimeClockScreen;
